import type { FC } from "react";
import { Show } from "shared/components";
import { cx } from "shared/utils";
import type { ReportPerson } from "api/owner-screening-report";
import { formatDate } from "../format";
import { SectionBlock } from "./SectionBlock";

/**
 * One person's card. Kept whole across a page break when printed — a card split
 * down the middle puts a figure under the wrong name.
 */
export const PersonCard: FC<{ person: ReportPerson; compact?: boolean }> = ({
  person,
  compact
}) => {
  const submitted = formatDate(person.submitted_on);

  return (
    <section className="break-inside-avoid rounded-xl border border-alpha-100 bg-background-1000 shadow-xs">
      <header className="flex flex-wrap items-baseline justify-between gap-2 border-b border-alpha-40 px-6 py-4">
        <div className="flex flex-wrap items-baseline gap-2">
          <h3 className="text-lg font-bold text-content-1000">{person.name}</h3>
          <span className="text-sm text-content-400">{person.role_label}</span>
        </div>
        <Show if={Boolean(submitted)}>
          <span className="text-sm text-content-400">
            Submitted {submitted}
          </span>
        </Show>
      </header>

      {/* Two subcards abreast, one per column on a narrow frame. Sections used
          to stack as full-width rules, which gave a nine-section card no
          structure to scan. */}
      <div
        className={cx(
          "grid gap-4 p-4 md:p-5",
          compact ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2"
        )}
      >
        {person.sections.map((section) => (
          <SectionBlock key={section.section} section={section} />
        ))}
      </div>
    </section>
  );
};
